import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { deleteAssistant } from "../../actions/assistants";
import { RoutesPaths } from "../../Router/constant";
import ConfirmDialog from "../../Components/ConfirmDialog";
import CardHeading from "./CardHeading";

/**
 * Removing an assistant.
 *
 * DELETE /assistants/:id is a soft delete: nothing leaves the database, but
 * the assistant is hidden everywhere and its widget stops loading, and it
 * stops counting towards the plan's assistant and page allowances - which is
 * how an account makes room for another one, or for a smaller plan.
 */
export default function DeleteAssistantCard({ assistant }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const id = assistant?.id;
  const name = assistant?.name || "this assistant";

  const remove = async () => {
    setBusy(true);
    setError("");

    const result = await dispatch(deleteAssistant(id));

    setBusy(false);
    setConfirming(false);

    if (!result?.success) {
      setError(result?.message || "We couldn't delete that assistant.");
      return;
    }

    // This page is about the assistant that no longer exists; the action has
    // already pointed the app at another one.
    navigate(RoutesPaths.DASHBOARD);
  };

  return (
    <section className="dash-card ist-danger-card">
      <CardHeading icon={Trash2} tone="red" title="Delete assistant" />

      <p className="ist-danger-card__text">
        The chat widget stops loading on your website straight away, and{" "}
        <strong>{name}</strong> disappears from your dashboard. It also stops
        counting towards the assistants and pages your plan allows.
      </p>

      {error && <p className="auth-error">{error}</p>}

      <button
        type="button"
        className="onboarding__btn onboarding__btn--secondary ist-danger-card__btn"
        onClick={() => setConfirming(true)}
        disabled={busy || !id}
      >
        {busy ? (
          <span className="install-status-spinner" aria-hidden="true" />
        ) : (
          <Trash2 size={16} strokeWidth={2.1} />
        )}
        {busy ? "Deleting…" : "Delete assistant"}
      </button>

      <ConfirmDialog
        open={confirming}
        title={`Delete ${name}?`}
        message="Its chat widget stops working on your website straight away and it disappears from your dashboard. Conversations already collected are kept."
        confirmLabel={busy ? "Deleting…" : "Yes, delete it"}
        cancelLabel="Keep it"
        onConfirm={remove}
        onCancel={() => !busy && setConfirming(false)}
      />
    </section>
  );
}
